<template>
  <a
    v-if="shouldShow"
    class="link-card"
    :href="preview.final_url || preview.url || url"
    target="_blank"
    rel="noopener noreferrer"
  >
    <img
      class="link-card-icon"
      :src="preview.icon || iconUrl"
      :alt="preview.title || preview.host || '站点图标'"
      loading="lazy"
      @error="onIconError"
    />
      <div class="link-card-main">
        <div class="link-card-title">{{ preview.title }}</div>
        <div v-if="preview.description" class="link-card-desc">{{ preview.description }}</div>
      <div class="link-card-meta">
        <span class="link-card-host">{{ preview.site_name || preview.host }}</span>
        <span class="link-card-url">{{ preview.final_url || preview.url || url }}</span>
      </div>
    </div>
  </a>
</template>

<script>
import { getLinkPreview } from '@/api'

const cdnBase = process.env.VUE_APP_CDN_BASE || ''

// 链接预览请求去重:同一 url 全局只发一次,结果 LRU 缓存复用
// (同帖多组件渲染、后退重进、评论重复链接 均命中缓存,避免重复打接口)
const PREVIEW_CACHE_CAP = 200
const previewCache = new Map()   // url -> 预览数据(插入序,超 cap 删最早)
const pendingMap = new Map()     // url -> 进行中的 Promise(合并并发请求)

function fetchPreview(url) {
  // 命中缓存:提到队尾(LRU)
  if (previewCache.has(url)) {
    const v = previewCache.get(url)
    previewCache.delete(url)
    previewCache.set(url, v)
    return Promise.resolve(v)
  }
  // 已有进行中请求:复用同一个 Promise,避免并发重复打
  if (pendingMap.has(url)) return pendingMap.get(url)
  const p = getLinkPreview({ url })
    .then((res) => {
      const data = res.data || { url }
      if (previewCache.size >= PREVIEW_CACHE_CAP) {
        previewCache.delete(previewCache.keys().next().value)
      }
      previewCache.set(url, data)
      return data
    })
  pendingMap.set(url, p)
  p.finally(() => pendingMap.delete(url))   // 失败不入缓存,下次允许重试
  return p
}

export default {
  name: 'LinkCard',
  props: {
    url: { type: String, required: true },
  },
  data() {
    return {
      preview: null,
      iconErrored: false,
    }
  },
  computed: {
    shouldShow() {
      const title = (this.preview?.title || '').trim()
      const desc = (this.preview?.description || '').trim()
      return !!(title || desc)
    },
    iconUrl() {
      const path = `/api/site/icon?url=${encodeURIComponent(this.url)}`
      if (!cdnBase || cdnBase === '/') return path
      return `${cdnBase.replace(/\/$/, '')}${path}`
    },
  },
  watch: {
    url: {
      immediate: true,
      handler() {
        this.load()
      },
    },
  },
  methods: {
    async load() {
      if (!this.url) {
        this.preview = null
        return
      }
      this.iconErrored = false
      try {
        this.preview = await fetchPreview(this.url)
      } catch (e) {
        this.preview = {
          url: this.url,
          final_url: this.url,
          title: '',
          description: '',
          host: this.safeHost(this.url),
          site_name: '',
          icon: this.iconUrl,
        }
      }
    },
    onIconError(event) {
      if (this.iconErrored) return
      this.iconErrored = true
      event.target.src = this.iconUrl
    },
    safeHost(url) {
      try {
        return new URL(url).host
      } catch (e) {
        return url
      }
    },
  },
}
</script>

<style scoped>
.link-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-top: 12px;
  padding: 14px;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  background: linear-gradient(180deg, var(--surface), var(--surface-2));
  transition: border-color .15s, transform .15s, box-shadow .15s;
  min-width: 0;
  overflow: hidden;
}
.link-card:hover {
  border-color: var(--primary);
  transform: translateY(-1px);
  box-shadow: var(--shadow-md);
}
.link-card-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  flex-shrink: 0;
  object-fit: cover;
  background: var(--surface);
  border: 1px solid var(--border);
}
.link-card-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  word-break: break-word;
  overflow-wrap: anywhere;
}
.link-card-title {
  color: var(--text);
  font-size: 15px;
  font-weight: 700;
  line-height: 1.4;
  word-break: break-word;
  overflow-wrap: anywhere;
}
.link-card-desc {
  color: var(--text-2);
  font-size: 13px;
  line-height: 1.6;
  word-break: break-word;
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.link-card-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex-wrap: wrap;
  color: var(--text-3);
  font-size: 12px;
}
.link-card-host {
  color: var(--primary);
  font-weight: 600;
}
.link-card-url {
  min-width: 0;
  word-break: break-word;
  overflow-wrap: anywhere;
}

@media (max-width: 640px) {
  .link-card {
    padding: 12px;
    gap: 10px;
  }
  .link-card-icon {
    width: 36px;
    height: 36px;
    border-radius: 8px;
  }
}
</style>
